import { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { Facebook, Linkedin, ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { COMPANY } from "@/lib/seo";
import hadleyLogo from "@/assets/hadley-logo.webp";

const services = [
  { label: "Electrical Services", href: "/electrical-services" },
  { label: "Commercial Electrical Contractor", href: "/electrical-services/commercial-electrical-contractor" },
  { label: "Industrial Electrical", href: "/electrical-services/industrial-electrical-contractor" },
  { label: "Electrical Service & Repair", href: "/electrical-services/electrical-service-and-repair" },
  { label: "Preventative Maintenance", href: "/electrical-services/preventative-maintenance" },
  { label: "Panel Upgrades", href: "/electrical-services/electrical-panel-upgrades" },
  { label: "Switchgear Installation", href: "/electrical-services/switchgear-installation" },
  { label: "Transformer Installation", href: "/electrical-services/transformer-installation" },
  { label: "High Voltage Electrical", href: "/electrical-services/high-voltage-electrical" },
  { label: "Generator Installation", href: "/electrical-services/generator-installation" },
  { label: "Motor Control Centers", href: "/electrical-services/motor-control-centers" },
  { label: "PLC & Motor Controls", href: "/electrical-services/plc-motor-controls" },
  { label: "Fire Alarm Systems", href: "/electrical-services/fire-alarm-systems" },
  { label: "Data Cabling & Low Voltage", href: "/electrical-services/data-cabling-low-voltage" },
  { label: "Electrical Design-Build", href: "/electrical-services/electrical-design-build" },
  { label: "Electrical Safety Inspections", href: "/electrical-services/electrical-safety-inspections" },
  { label: "24/7 Emergency Electrical", href: "/electrical-services/emergency-electrical-service" },
  { label: "HVAC Services", href: "/hvac-services" },
  { label: "Commercial HVAC", href: "/hvac-services/commercial-hvac" },
  { label: "Industrial HVAC", href: "/hvac-services/industrial-hvac" },
  { label: "Residential HVAC", href: "/hvac-services/residential-hvac" },
  { label: "HVAC Installation", href: "/hvac-services/hvac-installation" },
  { label: "HVAC Repair & Service", href: "/hvac-services/hvac-service-repair" },
  { label: "HVAC Preventative Maintenance", href: "/hvac-services/hvac-preventative-maintenance" },
  { label: "Building Automation Systems", href: "/hvac-services/building-automation-systems" },
  { label: "Temperature Controls", href: "/hvac-services/temperature-controls" },
  { label: "Ductwork Installation", href: "/hvac-services/ductwork-installation" },
  { label: "Heat Pump Installation", href: "/hvac-services/heat-pump-installation" },
  { label: "Furnace Installation & Repair", href: "/hvac-services/furnace-installation-repair" },
  { label: "AC Installation & Repair", href: "/hvac-services/ac-installation-repair" },
  { label: "Geothermal Heating & Cooling", href: "/hvac-services/geothermal-heating-cooling" },
  { label: "Indoor Air Quality", href: "/hvac-services/indoor-air-quality" },
  { label: "Energy Management", href: "/energy-management" },
  { label: "Lighting Retrofits & LED", href: "/energy-management/lighting-retrofits" },
  { label: "Lighting Design", href: "/energy-management/lighting-design" },
  { label: "Energy Audits", href: "/energy-management/energy-audits" },
  { label: "Energy Bill Analysis", href: "/energy-management/energy-bill-analysis" },
  { label: "Guaranteed Energy Savings", href: "/energy-management/guaranteed-energy-savings" },
  { label: "Energy Efficiency Consulting", href: "/energy-management/energy-efficiency-consulting" },
  { label: "Stadium & Sports Lighting", href: "/stadium-sports-lighting" },
  { label: "Football Stadium Lighting", href: "/stadium-sports-lighting/football-stadium-lighting" },
  { label: "Baseball Field Lighting", href: "/stadium-sports-lighting/baseball-field-lighting" },
  { label: "Tennis Court Lighting", href: "/stadium-sports-lighting/tennis-court-lighting" },
];

const industries = [
  { label: "All Industries", href: "/industries" },
  { label: "Manufacturing & Automotive", href: "/industries/manufacturing-electrical" },
  { label: "Automotive Plant Electrical", href: "/industries/automotive-plant-electrical" },
  { label: "Warehouse & Distribution", href: "/industries/warehouse-electrical" },
  { label: "Healthcare & Hospitals", href: "/industries/healthcare-facility-compliance" },
  { label: "Schools & Education", href: "/industries/school-electrical-contractor" },
  { label: "Hotels & Hospitality", href: "/industries/hotel-electrical-contractor" },
  { label: "Churches & Religious", href: "/industries/church-electrical-contractor" },
  { label: "Government & Municipal", href: "/industries/municipal-electrical" },
  { label: "Restaurants & Retail", href: "/industries/restaurant-electrical" },
  { label: "Data Center Electrical", href: "/industries/data-center-electrical" },
  { label: "Water Treatment Electrical", href: "/industries/water-treatment-electrical" },
  { label: "Food & Beverage Manufacturing", href: "/industries/food-beverage-manufacturing" },
];

const serviceAreas = [
  { label: "All Service Areas", href: "/service-area" },
  { label: "Louisville, KY", href: "/service-area/louisville-ky" },
  { label: "Lexington, KY", href: "/service-area/lexington-ky" },
  { label: "Elizabethtown, KY", href: "/service-area/elizabethtown-ky" },
  { label: "Frankfort, KY", href: "/service-area/frankfort-ky" },
  { label: "Bowling Green, KY", href: "/service-area/bowling-green-ky" },
  { label: "Shepherdsville, KY", href: "/service-area/shepherdsville-ky" },
  { label: "Mt. Washington, KY", href: "/service-area/mt-washington-ky" },
  { label: "Jeffersontown, KY", href: "/service-area/jeffersontown-ky" },
  { label: "St. Matthews, KY", href: "/service-area/st-matthews-ky" },
  { label: "Bardstown, KY", href: "/service-area/bardstown-ky" },
  { label: "La Grange, KY", href: "/service-area/la-grange-ky" },
  { label: "Shelbyville, KY", href: "/service-area/shelbyville-ky" },
  { label: "New Albany, IN", href: "/service-area/new-albany-in" },
  { label: "Jeffersonville, IN", href: "/service-area/jeffersonville-in" },
  { label: "Clarksville, IN", href: "/service-area/clarksville-in" },
];

const company = [
  { label: "About Us", href: "/about" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Reviews", href: "/reviews" },
  { label: "Blog", href: "/blog" },
  { label: "Careers", href: "/about/careers" },
  { label: "Safety", href: "/about/safety" },
  { label: "FAQ", href: "/about/faq" },
  { label: "Contact", href: "/contact" },
  { label: "Request Estimate", href: "/request-estimate" },
  { label: "Sitemap", href: "/sitemap" },
  { label: "Privacy Policy", href: "/privacy-policy" },
];

/* Most-read guides — moved here from the homepage so their link equity survives. */
const guides = [
  { label: "Commercial HVAC Guide Louisville", href: "/guide/commercial-hvac-guide-louisville" },
  { label: "Energy Management Louisville 2026", href: "/guide/energy-management-louisville-2026" },
  { label: "Commercial Electrical Costs Louisville", href: "/guide/commercial-electrical-costs-louisville-ky" },
  { label: "Whole-House Surge Protection Louisville", href: "/blog/whole-house-surge-protection-louisville-ky" },
  { label: "Commercial Energy Audit Louisville KY", href: "/blog/energy-audit-louisville-ky" },
  { label: "Commercial Lighting Retrofit Louisville KY", href: "/blog/commercial-lighting-retrofit-louisville-ky" },
  { label: "HVAC Services in Bardstown KY", href: "/blog/hvac-services-bardstown-ky" },
  { label: "24-Hour Emergency Electrician Louisville", href: "/blog/24-hour-emergency-electrician-louisville" },
  { label: "Generator Installation Cost Louisville", href: "/blog/generator-installation-louisville-ky" },
  { label: "Commercial HVAC Contractor Louisville KY", href: "/blog/commercial-hvac-contractor-louisville-ky" },
  { label: "Commercial Air Conditioning Louisville", href: "/blog/commercial-air-conditioning-louisville-guide" },
  { label: "Commercial Electrical Repair Issues", href: "/blog/commercial-electrical-repair-common-issues" },
  { label: "One Company for Electrical & HVAC", href: "/blog/can-one-company-handle-both-my-electrical-and-havc" },
];

const LinkList = ({ links, dense = false }: { links: { label: string; href: string }[]; dense?: boolean }) => (
  <ul className={`mt-4 space-y-1.5 ${dense ? "sm:columns-2 sm:gap-6" : ""}`}>
    {links.map(link => (
      <li key={link.href + link.label}>
        <Link to={link.href} className="font-body text-sm text-charcoal-foreground/70 hover:text-white">{link.label}</Link>
      </li>
    ))}
  </ul>
);

const Heading = ({ title }: { title: string }) => (
  <h3 className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary-light">{title}</h3>
);

/**
 * Below 768px each footer column is a native <details> accordion, closed by
 * default. From md up the accordion is forced open and the marker hidden, so
 * the desktop footer is visually unchanged. Every link stays in the DOM.
 */
const Column = ({
  title,
  links,
  dense = false,
  open,
  children,
}: {
  title: string;
  links: { label: string; href: string }[];
  dense?: boolean;
  open: boolean;
  children?: ReactNode;
}) => (
  <details open={open} className="group [&>summary]:list-none [&>summary::-webkit-details-marker]:hidden">
    <summary className="flex cursor-pointer items-center justify-between md:cursor-default md:pointer-events-none">
      <Heading title={title} />
      <ChevronDown className="h-4 w-4 text-primary-light transition-transform group-open:rotate-180 md:hidden" aria-hidden="true" />
    </summary>
    <LinkList links={links} dense={dense} />
    {children}
  </details>
);

const Footer = () => {
  const isMobile = useIsMobile();
  const open = !isMobile;
  return (
  <footer className="bg-charcoal text-charcoal-foreground">
    <div className="container py-12">
      <div className="grid gap-6 md:gap-10 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1.4fr]">
        <Column title="Services" links={services} dense open={open} />
        <Column title="Industries" links={industries} open={open} />
        <Column title="Service Areas" links={serviceAreas} dense open={open} />
        <Column title="Company" links={company} open={open}>
          <div className="mt-8">
            <Heading title="Guides & Resources" />
            <LinkList links={guides} dense />
          </div>
        </Column>
      </div>
    </div>

    {/* NAP block */}
    <div className="border-t border-charcoal-foreground/12">
      <div className="container grid gap-8 py-8 md:grid-cols-3">
        <div>
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={hadleyLogo} alt="Hadley Energy Solutions logo" className="h-10 w-auto rounded-[4px] bg-white p-0.5" width="65" height="40" />
            <span className="font-heading text-sm font-bold text-white">{COMPANY.name}</span>
          </Link>
          <address className="mt-4 not-italic font-body text-sm leading-relaxed text-charcoal-foreground/70">
            {COMPANY.address}<br />
            <a href={`tel:${COMPANY.phoneTel}`} data-cta="footer-nap" className="hover:text-white">{COMPANY.phone}</a><br />
            <a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a>
          </address>
        </div>
        <div className="font-body text-sm leading-relaxed text-charcoal-foreground/70">
          <p className="font-heading text-sm font-bold text-white">Hours</p>
          <p className="mt-2">Mon–Fri 8:00–4:30</p>
          <p><span className="text-signal">24/7 Emergency</span> service available</p>
          <p className="mt-4">Licensed: CE64623 · ME64500 · HM06538</p>
        </div>
        <div className="font-body text-sm text-charcoal-foreground/70">
          <p className="font-heading text-sm font-bold text-white">Follow</p>
          <div className="mt-3 flex gap-3">
            <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-8 w-8 items-center justify-center rounded-[4px] border border-charcoal-foreground/20 hover:border-primary hover:text-white">
              <Facebook className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a href={COMPANY.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="flex h-8 w-8 items-center justify-center rounded-[4px] border border-charcoal-foreground/20 hover:border-primary hover:text-white">
              <Linkedin className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>
          <a href={COMPANY.googleReviews} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block hover:text-white">Rate Us on Google</a>
        </div>
      </div>
    </div>

    <div className="border-t border-charcoal-foreground/12">
      <div className="container flex flex-col gap-2 py-6 pb-24 font-body text-xs text-charcoal-foreground/45 md:flex-row md:items-center md:justify-between md:pb-6">
        <span>© 2026 {COMPANY.name}. All rights reserved.</span>
        <span>
          Marketing &amp; AI Optimization by{" "}
          <a href="https://louisvilleweblab.com" target="_blank" rel="noopener" className="text-primary-light hover:underline">Louisville Web Lab</a>
        </span>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
